import React from 'react';
import { SpaceType } from 'src/features/spaces/types/space-type';
import { IssueStatus } from '../../api/types/response/issue.response';
import { useTranslation } from 'react-i18next';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { spaceTypesTranslations } from 'src/features/spaces/constants/space-types-translations';
import { useOrganizationDetails } from 'src/features/organization-details/hooks/api/use-organization-details';
import { FormatDate } from 'src/components/atoms/format-date';
import { TZDate } from '@date-fns/tz';
import { dateFormats } from 'src/utils/date-formats';
import { Status } from 'src/components/atoms/status';
import { issueStateColors } from '../../constants/issue-state/issue-state-colors';
import { spaceChangeColors } from '../../constants/issue-state/space-change/space-change-colors';
import { issueStateTranslations } from '../../constants/issue-state/issue-state-translations';
import { spaceChangeTranslations } from '../../constants/issue-state/space-change/space-change-translations';
import { useLocation, useNavigate } from '@tanstack/react-router';

export interface IssuesActionsSlotPayload {
  token: string;
  status: IssueStatus;
  comment?: string;
}

interface Props {
  token: string;
  reportedByEmail: string;
  spaceChangeRequested: boolean;
  spaceName: string;
  spaceType?: SpaceType;
  reportedAt: number;
  status: IssueStatus;
  actionsSlot: (payload: IssuesActionsSlotPayload) => React.ReactNode;
  actionedByEmail?: string;
  comment?: string;
}
export default function IssuesTableRow({
  token,
  reportedByEmail,
  spaceChangeRequested,
  spaceName,
  spaceType,
  reportedAt,
  status,
  actionsSlot,
  actionedByEmail,
  comment,
}: Props) {
  const { t } = useTranslation(['issues', 'common', 'spaces']);

  const { data: organizationDetails } = useOrganizationDetails();
  const tenantTimezone = organizationDetails?.timezone;

  const navigate = useNavigate();
  const location = useLocation();

  return (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      onClick={() =>
        navigate({
          to: '/tenant/issues/$issueToken',
          params: { issueToken: token },
          state: {
            backUrl: location.href,
          },
        })
      }>
      <TableCell>
        <Typography variant="body2">{reportedByEmail}</Typography>
      </TableCell>
      <TableCell>
        <Status
          color={spaceChangeColors[spaceChangeRequested.toString()]}
          label={t(
            spaceChangeTranslations[
              spaceChangeRequested.toString() as 'true' | 'false'
            ],
          )}
        />
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2">{spaceName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {spaceType && t(spaceTypesTranslations[spaceType])}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Typography color="inherit" variant="inherit">
            <FormatDate
              date={new TZDate(reportedAt, tenantTimezone)}
              format={`${dateFormats.day} ${dateFormats.monthShort}, ${dateFormats.yearShort}`}
            />
          </Typography>
          <Typography color="text.secondary" variant="inherit">
            <FormatDate
              date={new TZDate(reportedAt, tenantTimezone)}
              format={`${dateFormats.hours}:${dateFormats.minutes}`}
            />
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Status
            color={issueStateColors[status]}
            label={t(issueStateTranslations[status])}
          />
          {actionedByEmail && (
            <Typography variant="body2" color="text.secondary">
              {actionedByEmail}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ cursor: 'default' }}>
        {actionsSlot({
          token,
          status,
          comment,
        })}
      </TableCell>
    </TableRow>
  );
}
